<template>
  <div class="workbench">
    <!-- 顶部栏 -->
    <div class="workbench-header">
      <div class="stat-section">
        <div class="stat-item">
          <div class="stat-label">今日收入(小程序支付)</div>
          <div class="stat-value">¥ {{ statVisible ? todayIncome : '****' }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">今日订单</div>
          <div class="stat-value">{{ statVisible ? todayOrderCount : '****' }}</div>
        </div>
        <div class="stat-toggle" @click="statVisible = !statVisible">
          <el-icon :size="18">
            <View v-if="statVisible" />
            <Hide v-else />
          </el-icon>
        </div>
      </div>
      <div class="action-section">
        <el-button type="warning" @click="handleVerifyCoupon">
          核销券
        </el-button>
        <el-button type="success" @click="handleDepositAudit">
          存积分审核
        </el-button>
        <el-button type="danger" @click="handleWithdrawAudit">
          取积分审核
        </el-button>
        <el-button type="primary" @click="handleAdjustPoints">
          会员积分调整
        </el-button>
        <el-button @click="handleRefresh">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 主体 -->
    <div class="workbench-main">
      <!-- 左侧：桌面列表 -->
      <div class="main-left">
        <div class="section-header">
          <h3>桌面管理</h3>
        </div>
        <div class="desktop-list">
          <!-- 桌面列表 -->
          <div
            v-for="desk in desktopList"
            :key="desk.id"
            class="desktop-card"
          >
            <div class="desk-header">
              <div class="desk-info">
                <span class="desk-name">{{ desk.name }}</span>
                <span class="desk-points">基础积分：{{ desk.basePoints || '0' }}</span>
              </div>
              <div class="desk-actions">
                <el-button
                  size="small"
                  style="margin-right: 8px;"
                  @click="handleClearSeats(desk)"
                >
                  一键清空座位
                </el-button>
                <span class="desk-occupy">{{ getOccupiedCount(desk) }}/9</span>
              </div>
            </div>
            <div class="seats-grid">
              <div
                v-for="seatNum in 9"
                :key="seatNum"
                class="seat-item"
                :class="getSeatStatus(desk, seatNum)"
              >
                <template v-if="getSeatStatus(desk, seatNum) === 'idle'">
                  <span class="seat-num">{{ seatNum }}</span>
                  <span class="seat-label">空闲</span>
                  <el-button size="small" type="primary" text @click="openSitDownDialog(desk, seatNum)">上桌</el-button>
                </template>
                <template v-else>
                  <el-avatar :size="28" :src="getSeatAvatar(desk, seatNum)">
                    {{ getSeatUserName(desk, seatNum).charAt(0) }}
                  </el-avatar>
                  <span class="seat-name">{{ getSeatUserName(desk, seatNum) }}</span>
                  <span class="seat-phone">{{ getSeatPhone(desk, seatNum) }}</span>
                  <span class="seat-status" :class="getSeatStatus(desk, seatNum)">
                    {{ getSeatStatus(desk, seatNum) === 'reserved' ? '已预约' : '游戏中' }}
                  </span>
                  <el-button
                    v-if="getSeatStatus(desk, seatNum) === 'reserved'"
                    size="small" type="primary" text @click="openSitDownDialog(desk, seatNum)"
                  >上桌</el-button>
                  <el-button
                    v-if="getSeatStatus(desk, seatNum) === 'playing'"
                    size="small" type="danger" text @click="handleStandUp(desk, seatNum)"
                  >离开</el-button>
                </template>
              </div>
            </div>
          </div>
          <el-empty v-if="desktopList.length === 0 && !desktopLoading" description="暂无桌面" />
          <!-- 候桌列表 -->
          <div class="waiting-section">
            <div class="waiting-title">候桌列表 ({{ waitingList.length }}人)</div>
            <div class="waiting-list">
              <div v-for="(item, index) in waitingList" :key="item.id" class="waiting-item">
                <span class="waiting-index">{{ index + 1 }}</span>
                <el-avatar :size="24" :src="item.userInfo?.avatar ? getFullUrl(item.userInfo.avatar) : ''">
                  {{ item.userInfo?.name ? item.userInfo.name.charAt(0) : '?' }}
                </el-avatar>
                <span class="waiting-name">{{ item.userInfo?.name || '-' }}</span>
                <span class="waiting-phone">{{ item.userInfo?.phone || '-' }}</span>
                <el-button size="small" type="primary" text @click="openWaitingSitDownDialog(item)">
                  上桌
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：待出餐订单 -->
      <div class="main-right">
        <div class="section-header">
          <h3>待出餐订单</h3>
          <el-badge :value="pendingOrders.length" :hidden="pendingOrders.length === 0" />
          <div class="header-right">
            <el-switch
              v-model="soundEnabled"
              :active-icon="Bell"
              :inactive-icon="MuteNotification"
              inline-prompt
            />
            <el-button type="primary" size="small" @click="handleManualOrder">
              手动下单
            </el-button>
          </div>
        </div>
        <div class="order-list">
          <div
            v-for="order in pendingOrders"
            :key="order.id"
            class="order-card"
          >
            <div class="order-header">
              <span class="order-seat">{{ order.seatInfo || '未知桌位' }}</span>
              <span class="order-id">订单号：{{ order.id }}</span>
            </div>
            <div class="order-user">
              <div class="user-left">
                <el-avatar
                  v-if="order.userInfo"
                  :size="24"
                  :src="order.userInfo.avatar ? getFullUrl(order.userInfo.avatar) : ''"
                >
                  {{ order.userInfo.name ? order.userInfo.name.charAt(0) : '?' }}
                </el-avatar>
                <span class="user-name">{{ order.userInfo?.name || '-' }}</span>
                <span class="user-phone">{{ order.userInfo?.phone || '-' }}</span>
              </div>
              <div class="user-right">
                <span v-if="hasMoneyAmount(order.price)" class="order-amount">¥ {{ formatMoney(order.price) }}</span>
                <span v-if="hasPointsAmount(order.pointsPrice)" class="order-amount points">{{ formatPoints(order.pointsPrice) }} 积分</span>
                <span class="order-paytype">{{ getPayTypeName(order.payType) }}</span>
              </div>
            </div>
            <div class="order-goods">
              <div v-for="(item, idx) in order.goods" :key="idx" class="goods-item">
                <div class="goods-main-line">
                  <span class="goods-name">{{ item.name || '-' }}</span>
                  <span class="goods-meta">x{{ item.count || 1 }} · {{ getGoodsTotalDisplay(item) }}</span>
                </div>
                <div v-if="getGoodsSpecLines(item).length" class="goods-spec-lines">
                  <div v-for="(specLine, specIdx) in getGoodsSpecLines(item)" :key="`${idx}-spec-${specIdx}`" class="goods-spec-line">
                    {{ specLine }}
                  </div>
                </div>
              </div>
            </div>
            <div class="order-footer">
              <span class="order-time">{{ order.createTime }}</span>
              <div class="footer-btns">
                <el-button size="small" @click="handleCancelOrder(order)">
                  取消订单
                </el-button>
                <el-button type="success" size="small" @click="handleComplete(order)">
                  完成出餐
                </el-button>
              </div>
            </div>
          </div>
          <el-empty v-if="pendingOrders.length === 0 && !orderLoading" description="暂无待出餐订单" />
        </div>
      </div>
    </div>

    <!-- 上桌对话框 -->
    <el-dialog v-model="sitDownDialogVisible" title="上桌操作" width="400px">
      <el-form :model="sitDownForm" label-width="80px">
        <el-form-item label="用户">
          <el-select
            v-if="!sitDownForm.isReserved"
            v-model="sitDownForm.userId"
            placeholder="从候桌列表选择"
            style="width: 100%"
          >
            <el-option
              v-for="w in waitingList"
              :key="w.id"
              :label="`${w.userInfo?.name || '-'} (${w.userInfo?.phone || '-'})`"
              :value="w.userId"
            />
          </el-select>
          <span v-else>{{ sitDownForm.userName }}</span>
        </el-form-item>
        <el-form-item label="扣除积分">
          <el-input-number v-model="sitDownForm.points" :min="0" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="sitDownDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSitDown">确定</el-button>
      </template>
    </el-dialog>

    <!-- 候桌上桌对话框 -->
    <el-dialog v-model="waitingSitDlgVisible" title="选择座位上桌" width="400px">
      <el-form :model="waitingSitForm" label-width="80px">
        <el-form-item label="用户">
          <span>{{ waitingSitForm.userName }}</span>
        </el-form-item>
        <el-form-item label="选择桌子">
          <el-select v-model="waitingSitForm.desktopId" placeholder="选择桌子" style="width: 100%">
            <el-option v-for="d in desktopList" :key="d.id" :label="d.name" :value="d.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="选择座位">
          <el-select v-model="waitingSitForm.seatNumber" placeholder="选择座位" style="width: 100%">
            <el-option
              v-for="n in getAvailableSeats(waitingSitForm.desktopId)"
              :key="n" :label="`${n}号座`" :value="n"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="扣除积分">
          <el-input-number v-model="waitingSitForm.points" :min="0" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="waitingSitDlgVisible = false">取消</el-button>
        <el-button type="primary" @click="handleWaitingSitDown">确定</el-button>
      </template>
    </el-dialog>

    <!-- 存积分审核弹窗 -->
    <el-dialog v-model="depositDlgVisible" title="存积分审核" :z-index="1000" width="80vw" append-to-body>
      <el-table :data="depositList" style="min-height: 600px;">
        <el-table-column prop="userInfo.name" label="用户" />
        <el-table-column prop="userInfo.phone" label="手机号" />
        <el-table-column prop="points" label="积分数" />
        <el-table-column prop="createTime" label="申请时间" />
        <el-table-column label="操作" width="150">
          <template #default="{ row }">
            <el-button type="success" size="small" @click="handleApprove(row, 'approved')">通过</el-button>
            <el-button type="danger" size="small" @click="handleApprove(row, 'rejected')">拒绝</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-model:current-page="depositPage"
        :page-size="10"
        :total="depositTotal"
        layout="total, prev, pager, next"
        style="margin-top: 16px; justify-content: flex-end;"
        @current-change="fetchDepositList"
      />
    </el-dialog>

    <!-- 核销券弹窗 -->
    <el-dialog
      v-model="verifyCouponDlgVisible"
      title="核销券"
      width="500px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form label-width="80px">
        <el-form-item label="券码">
          <div style="display: flex; gap: 8px; width: 100%;">
            <el-input v-model="couponId" placeholder="请输入券码" style="flex: 1;" />
            <el-button type="primary" @click="handleQueryCoupon" :loading="couponLoading">查询</el-button>
          </div>
        </el-form-item>
      </el-form>
      <div v-if="couponInfo" class="coupon-info">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="券名称">{{ couponInfo.name }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag v-if="couponInfo.verifyTime" type="info">已核销</el-tag>
            <el-tag v-else type="success">未核销</el-tag>
          </el-descriptions-item>
          <el-descriptions-item v-if="couponInfo.goods" label="商品信息">
            <div class="goods-detail">
              <el-image
                :src="couponInfo.goods.image ? getFullUrl(couponInfo.goods.image) : ''"
                fit="cover"
                style="width: 60px; height: 60px; border-radius: 4px;"
              />
              <div class="goods-info">
                <div class="goods-name">{{ couponInfo.goods.name || '-' }}</div>
                <div class="goods-desc">{{ couponInfo.goods.description || '-' }}</div>
                <div class="goods-price">¥ {{ couponInfo.goods.price || '0' }}</div>
              </div>
            </div>
          </el-descriptions-item>
        </el-descriptions>
        <!-- 未核销且有商品时需要选择桌和座位 -->
        <template v-if="!couponInfo.verifyTime">
          <el-form v-if="couponInfo.goods" label-width="80px" style="margin-top: 16px;">
            <el-form-item label="选择桌">
              <el-select v-model="verifyCouponForm.desktopId" placeholder="请选择桌" style="width: 100%">
                <el-option
                  v-for="desktop in desktopList"
                  :key="desktop.id"
                  :label="desktop.name"
                  :value="desktop.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="选择座位">
              <el-select v-model="verifyCouponForm.seatNumber" placeholder="请选择座位" style="width: 100%">
                <el-option
                  v-for="n in getAvailableSeats(verifyCouponForm.desktopId)"
                  :key="n"
                  :label="`${n}号座`"
                  :value="n"
                />
              </el-select>
            </el-form-item>
          </el-form>
          <div style="margin-top: 16px; text-align: center;">
            <el-button type="success" size="large" @click="handleVerifyCouponSubmit" :loading="verifyLoading">
              确认核销
            </el-button>
          </div>
        </template>
      </div>
    </el-dialog>

    <!-- 取积分审核弹窗 -->
    <el-dialog v-model="withdrawDlgVisible" title="取积分审核" :z-index="1000" width="80vw" append-to-body>
      <el-table :data="withdrawList" style="min-height: 600px;">
        <el-table-column prop="userInfo.name" label="用户" />
        <el-table-column prop="userInfo.phone" label="手机号" />
        <el-table-column prop="points" label="积分数" />
        <el-table-column prop="createTime" label="申请时间" />
        <el-table-column label="操作" width="150">
          <template #default="{ row }">
            <el-button type="success" size="small" @click="handleApprove(row, 'approved')">通过</el-button>
            <el-button type="danger" size="small" @click="handleApprove(row, 'rejected')">拒绝</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-model:current-page="withdrawPage"
        :page-size="10"
        :total="withdrawTotal"
        layout="total, prev, pager, next"
        style="margin-top: 16px; justify-content: flex-end;"
        @current-change="fetchWithdrawList"
      />
    </el-dialog>

    <!-- 手动下单弹窗 -->
    <el-dialog v-model="manualOrderDlgVisible" title="手动下单" width="90vw" :z-index="1000" append-to-body :close-on-click-modal="false">
      <div class="manual-order-container">
        <!-- 左侧：基本信息 + 商品选择 -->
        <div class="order-left">
          <el-form label-width="80px" size="small">
            <el-form-item label="选择用户">
              <el-select
                v-model="manualOrderForm.userId"
                filterable
                remote
                reserve-keyword
                placeholder="搜索手机号"
                :remote-method="searchUsers"
                :loading="userSearchLoading"
                style="width: 100%"
              >
                <el-option
                  v-for="user in userOptions"
                  :key="user.value"
                  :label="user.label"
                  :value="user.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="选择桌">
              <el-select v-model="manualOrderForm.desktopId" placeholder="请选择桌" style="width: 100%">
                <el-option v-for="d in orderDeskList" :key="d.id" :label="d.name" :value="d.id" />
              </el-select>
            </el-form-item>
          </el-form>
          <!-- 商品分类和列表 -->
          <div class="goods-section">
            <div class="category-list">
              <div
                v-for="(cat, idx) in categoryList"
                :key="cat.id"
                class="category-item"
                :class="{ active: currentCategoryIdx === idx }"
                @click="currentCategoryIdx = idx"
              >
                {{ cat.name }}
              </div>
            </div>
            <div class="goods-list">
              <div v-for="goods in filteredGoodsList" :key="goods.id" class="goods-item">
                <el-image
                  :src="goods.image ? getFullUrl(goods.image) : ''"
                  fit="cover"
                  style="width: 50px; height: 50px; border-radius: 4px;"
                />
                <div class="goods-info">
                  <div class="goods-name">{{ goods.name }}</div>
                  <div class="goods-desc">{{ goods.description || '' }}</div>
                  <div class="goods-price">{{ getGoodsUnitDisplay(goods) }}</div>
                </div>
                <div class="goods-actions">
                  <el-button v-if="getCartCount(goods.id) > 0" size="small" circle @click="removeFromCart(goods)">-</el-button>
                  <span v-if="getCartCount(goods.id) > 0" class="cart-count">{{ getCartCount(goods.id) }}</span>
                  <el-button size="small" type="primary" circle @click="addToCart(goods)">+</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 右侧：购物车 + 结算 -->
        <div class="order-right">
          <div class="cart-title">已选商品</div>
          <div class="cart-list">
            <div v-for="item in cartItems" :key="item.cartKey" class="cart-item">
              <div class="item-main">
                <span class="item-name">{{ getGoodsDisplayName(item) }}</span>
                <span class="item-price">{{ getGoodsTotalDisplay(item) }}</span>
              </div>
              <div class="item-actions">
                <el-button size="small" circle @click="decreaseCartItem(item.cartKey)">-</el-button>
                <span class="item-count">x{{ item.count }}</span>
                <el-button size="small" type="primary" circle @click="increaseCartItem(item)">+</el-button>
              </div>
            </div>
            <el-empty v-if="cartItems.length === 0" description="暂无商品" :image-size="60" />
          </div>
          <div class="cart-total">
            <span>合计：</span>
            <span v-if="hasMoneyAmount(cartMoneyTotal)" class="total-price">¥{{ cartMoneyTotal }}</span>
            <span v-if="hasPointsAmount(cartPointsTotal)" class="total-price points">{{ formatPoints(cartPointsTotal) }} 积分</span>
          </div>
          <el-form label-width="80px" size="small" style="margin-top: 16px;">
            <el-form-item label="支付方式">
              <el-select v-model="manualOrderForm.payType" placeholder="请选择" style="width: 100%">
                <el-option v-for="option in manualPayTypeOptions" :key="option.value" :label="option.label" :value="option.value" />
              </el-select>
            </el-form-item>
            <el-form-item label="实收金额">
              <el-input-number
                v-model="manualOrderForm.receivedAmount"
                :min="0"
                :precision="2"
                :disabled="!hasMoneyAmount(cartMoneyTotal)"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="manualOrderForm.remark" type="textarea" :rows="2" placeholder="可选" />
            </el-form-item>
          </el-form>
          <div style="text-align: center; margin-top: 16px;">
            <el-button type="primary" size="large" :loading="manualOrderLoading" @click="submitManualOrder">
              提交订单
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <GoodsSpecSelectDialog
      v-model:visible="specDialogVisible"
      :goods="specDialogGoods"
      :initial-selected-specs="specDialogSpecs"
      @confirm="handleSpecDialogConfirm"
    />

    <!-- 会员积分调整弹窗 -->
    <el-dialog v-model="adjustPointsDlgVisible" title="会员积分调整" width="500px" append-to-body :close-on-click-modal="false">
      <el-form label-width="80px" size="default">
        <el-form-item label="选择用户">
          <el-select
            v-model="adjustPointsForm.userId"
            filterable
            remote
            reserve-keyword
            placeholder="搜索手机号"
            :remote-method="searchUsersForPoints"
            :loading="adjustPointsSearchLoading"
            style="width: 100%"
            @change="handleSelectUserForPoints"
          >
            <el-option
              v-for="user in adjustPointsUserOptions"
              :key="user.value"
              :label="user.label"
              :value="user.value"
            />
          </el-select>
        </el-form-item>
        <!-- 用户信息 -->
        <div v-if="selectedUserInfo" class="user-info-card">
          <el-avatar :size="60" :src="selectedUserInfo.avatar" />
          <div class="user-detail">
            <div class="user-name">{{ selectedUserInfo.name || '-' }}</div>
            <div class="user-phone">{{ selectedUserInfo.phone || '-' }}</div>
            <div class="user-points">当前积分：<span class="points-value">{{ selectedUserInfo.points }}</span></div>
          </div>
        </div>
        <el-form-item label="操作类型">
          <el-radio-group v-model="adjustPointsForm.type">
            <el-radio value="add">增加积分</el-radio>
            <el-radio value="reduce">减少积分</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="积分值">
          <el-input-number v-model="adjustPointsForm.amount" :min="1" :precision="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="adjustPointsForm.reason" type="textarea" :rows="3" placeholder="请输入调整原因" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="adjustPointsDlgVisible = false">取消</el-button>
        <el-button type="primary" :loading="adjustPointsLoading" @click="submitAdjustPoints">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { Refresh, Bell, MuteNotification, View, Hide } from '@element-plus/icons-vue';
import { useApi } from '@/hooks/useApi';
import { ElMessage, ElMessageBox } from 'element-plus';
import Decimal from 'decimal.js';
import GoodsEditorDialog from '../goods/components/goods-editor-dialog.vue';
import GoodsSpecSelectDialog from './components/goods-spec-select-dialog.vue';
import {
  buildGoodsCartKey,
  getGoodsDisplayName,
  getGoodsSpecLines,
  normalizeGoodsSpecs,
  type GoodsSpec,
  type SelectedGoodsSpec,
} from '@/utils/goodsSpecs';

interface GoodsItem {
  name: string;
  count: number;
  price?: string;
  discountPrice?: string;
  total?: string;
  isPointsExchange?: boolean | null;
  pointsPrice?: string;
  totalPoints?: string;
  selectedSpecs?: SelectedGoodsSpec[];
  specText?: string;
}

interface OrderItem {
  id: number;
  seatInfo?: string;
  goods?: GoodsItem[];
  createTime: string;
  status: string;
  price?: string;
  pointsPrice?: string;
  payType?: string;
  userInfo?: {
    avatar?: string;
    name?: string;
    phone?: string;
  };
}

// 支付方式映射
const payTypeMap: Record<string, string> = {
  wechat: '微信支付',
  cash: '现金支付',
  coins: 'D币支付',
  points: '积分支付',
  coupon: '券兑换',
  other: '其他',
};

const getPayTypeName = (payType?: string) => {
  if (!payType) {
    return '-';
  }
  return payTypeMap[payType] || payType;
};

const formatMoney = (value?: string | number) => new Decimal(value || 0).toFixed(2);

const formatPoints = (value?: string | number) => new Decimal(value || 0).toString();

const hasMoneyAmount = (value?: string | number) => new Decimal(value || 0).gt(0);

const hasPointsAmount = (value?: string | number) => new Decimal(value || 0).gt(0);

type GoodsDisplayItem = {
  isPointsExchange?: boolean | null;
  price?: string;
  pointsPrice?: string;
  total?: string;
  totalPoints?: string;
  count?: number;
  discountPrice?: string;
  selectedSpecs?: SelectedGoodsSpec[];
  specText?: string;
};

const isPointsGoods = (goods: GoodsDisplayItem) => Boolean(goods.isPointsExchange) || hasPointsAmount(goods.totalPoints || goods.pointsPrice);

const getGoodsUnitDisplay = (goods: GoodsDisplayItem) => {
  if (isPointsGoods(goods)) {
    return `${formatPoints(goods.pointsPrice || goods.price)} 积分`;
  }
  return `¥${formatMoney(goods.discountPrice || goods.price)}`;
};

const getGoodsTotalDisplay = (goods: GoodsDisplayItem) => {
  if (isPointsGoods(goods)) {
    const totalPoints = goods.totalPoints
      || new Decimal(goods.pointsPrice || goods.price || 0)
        .times(goods.count || 0)
        .toString();
    return `${formatPoints(totalPoints)} 积分`;
  }
  const totalPrice = goods.total
    || new Decimal(goods.price || 0)
      .times(goods.count || 0)
      .toString();
  return `¥${formatMoney(totalPrice)}`;
};

const getFullUrl = (url: string) => {
  if (!url) {
    return '';
  }
  if (url.startsWith('http')) {
    return url;
  }
  return useApi().baseUrl + url;
};

interface SeatItem {
  seatNumber: number;
  userId?: number;
  status: 'idle' | 'reserved' | 'playing';
  userInfo?: {
    name?: string;
    avatar?: string;
    phone?: string;
  };
}

interface DesktopItem {
  id: number;
  name: string;
  basePoints?: string;
  seats?: SeatItem[];
}

interface WaitingItem {
  id: number;
  userId: number;
  createTime?: string;
  userInfo?: {
    name?: string;
    avatar?: string;
    phone?: string;
  };
}

const todayIncome = ref('0.00');
const todayOrderCount = ref(0);
const statVisible = ref(false);
const desktopLoading = ref(false);
const orderLoading = ref(false);
const desktopList = ref<DesktopItem[]>([]);
const orderDeskList = ref<DesktopItem[]>([]);
const pendingOrders = ref<OrderItem[]>([]);
const waitingList = ref<WaitingItem[]>([]);

// 上桌对话框
const sitDownDialogVisible = ref(false);
const sitDownForm = ref({
  desktopId: 0, seatNumber: 0, userId: undefined as number | undefined, points: 0, isReserved: false, userName: '',
});

// 候桌上桌对话框
const waitingSitDlgVisible = ref(false);
const waitingSitForm = ref({
  userId: 0, desktopId: undefined as number | undefined, seatNumber: undefined as number | undefined,
  points: 0, userName: '', waitingId: 0,
});

// 积分审核弹窗
const depositDlgVisible = ref(false);
const withdrawDlgVisible = ref(false);
const depositList = ref<any[]>([]);
const withdrawList = ref<any[]>([]);
const depositPage = ref(1);
const depositTotal = ref(0);
const withdrawPage = ref(1);
const withdrawTotal = ref(0);

// 核销券弹窗
const verifyCouponDlgVisible = ref(false);
const couponId = ref('');
const couponInfo = ref<any>(null);
const couponLoading = ref(false);
const verifyLoading = ref(false);
const verifyCouponForm = ref({
  desktopId: undefined as number | undefined,
  seatNumber: undefined as number | undefined,
});

// 手动下单弹窗
interface CategoryItem { id: number; name: string; }
interface GoodsItemFull {
  id: number;
  name: string;
  price: string;
  image?: string;
  categoryId?: number;
  description?: string;
  stock?: number;
  isPointsExchange?: boolean | null;
  pointsPrice?: string;
  specs?: GoodsSpec[];
}
interface CartItem extends GoodsItemFull {
  cartKey: string;
  goodsId: number;
  selectedSpecs: SelectedGoodsSpec[];
  specText: string;
  count: number;
}

const manualOrderDlgVisible = ref(false);
const manualOrderLoading = ref(false);
const manualOrderForm = ref({
  userId: undefined as number | undefined,
  desktopId: undefined as number | undefined,
  payType: 'cash' as string,
  receivedAmount: 0,
  remark: '',
});
const userSearchLoading = ref(false);
const userOptions = ref<{ label: string; value: number; openid: string }[]>([]);

// 会员积分调整
interface UserInfo {
  id: number;
  avatar: string;
  name: string;
  phone: string;
  points: number;
}
const adjustPointsDlgVisible = ref(false);
const adjustPointsLoading = ref(false);
const adjustPointsSearchLoading = ref(false);
const adjustPointsUserOptions = ref<{ label: string; value: number; userInfo: UserInfo }[]>([]);
const selectedUserInfo = ref<UserInfo | null>(null);
const adjustPointsForm = ref({
  userId: undefined as number | undefined,
  type: 'add' as 'add' | 'reduce',
  amount: 1,
  reason: '',
});

const categoryList = ref<CategoryItem[]>([]);
const goodsList = ref<GoodsItemFull[]>([]);
const currentCategoryIdx = ref(0);
const cart = ref<Map<string, CartItem>>(new Map());
const specDialogVisible = ref(false);
const specDialogGoods = ref<GoodsItemFull | undefined>();
const specDialogSpecs = ref<SelectedGoodsSpec[]>([]);

const filteredGoodsList = computed(() => {
  const category = categoryList.value[currentCategoryIdx.value];
  if (!category) {
    return goodsList.value;
  }
  return goodsList.value.filter((goodsItem) => goodsItem.categoryId === category.id);
});

const cartItems = computed(() => Array.from(cart.value.values()));
const cartMoneyTotal = computed(() => {
  let total = new Decimal(0);
  cart.value.forEach((item) => {
    if (!item.isPointsExchange) {
      total = total.plus(new Decimal(item.price || 0).times(item.count));
    }
  });
  return total.toFixed(2);
});
const cartPointsTotal = computed(() => {
  let total = new Decimal(0);
  cart.value.forEach((item) => {
    if (item.isPointsExchange) {
      total = total.plus(new Decimal(item.pointsPrice || item.price || 0).times(item.count));
    }
  });
  return total.toString();
});
const manualPayTypeOptions = computed(() => {
  if (!hasMoneyAmount(cartMoneyTotal.value) && hasPointsAmount(cartPointsTotal.value)) {
    return [{ label: '积分支付', value: 'points' }];
  }
  return [
    { label: '现金', value: 'cash' },
    { label: '其他', value: 'other' },
  ];
});

// 监听购物车合计变化，自动更新实收金额与默认支付方式
watch(cartMoneyTotal, (newVal) => {
  manualOrderForm.value.receivedAmount = Number(newVal);
  if (!hasMoneyAmount(newVal) && hasPointsAmount(cartPointsTotal.value)) {
    manualOrderForm.value.payType = 'points';
    return;
  }
  if (manualOrderForm.value.payType === 'points') {
    manualOrderForm.value.payType = 'cash';
  }
});

watch(cartPointsTotal, (newVal) => {
  if (!hasMoneyAmount(cartMoneyTotal.value) && hasPointsAmount(newVal)) {
    manualOrderForm.value.payType = 'points';
    return;
  }
  if (manualOrderForm.value.payType === 'points') {
    manualOrderForm.value.payType = 'cash';
  }
});

const getCartCount = (goodsId: number) => cartItems.value
  .filter((item) => item.goodsId === goodsId)
  .reduce((total, item) => total + item.count, 0);

const addResolvedGoodsToCart = (goods: GoodsItemFull, selectedSpecs: SelectedGoodsSpec[], specText: string) => {
  const cartKey = buildGoodsCartKey(goods.id, selectedSpecs);
  const existing = cart.value.get(cartKey);
  const currentCount = existing?.count || 0;
  const stock = goods.stock ?? 999;
  if (currentCount >= stock) {
    ElMessage.warning(`${goods.name} 库存不足`);
    return;
  }
  if (existing) {
    existing.count++;
  } else {
    cart.value.set(cartKey, {
      ...goods,
      cartKey,
      goodsId: goods.id,
      selectedSpecs,
      specText,
      count: 1,
    });
  }
  cart.value = new Map(cart.value);
};

const openSpecDialog = (goods: GoodsItemFull, initialSelectedSpecs: SelectedGoodsSpec[] = []) => {
  specDialogGoods.value = goods;
  specDialogSpecs.value = initialSelectedSpecs;
  specDialogVisible.value = true;
};

const handleSpecDialogConfirm = ({ selectedSpecs, specText }: { selectedSpecs: SelectedGoodsSpec[]; specText: string }) => {
  if (!specDialogGoods.value) {
    return;
  }
  addResolvedGoodsToCart(specDialogGoods.value, selectedSpecs, specText);
};

const addToCart = (goods: GoodsItemFull) => {
  if (normalizeGoodsSpecs(goods.specs).length > 0) {
    openSpecDialog(goods);
    return;
  }
  addResolvedGoodsToCart(goods, [], '');
};

const increaseCartItem = (goods: CartItem) => {
  if (normalizeGoodsSpecs(goods.specs).length > 0) {
    openSpecDialog(goods, goods.selectedSpecs);
    return;
  }
  addResolvedGoodsToCart(goods, goods.selectedSpecs, goods.specText);
};

const decreaseCartItem = (cartKey: string) => {
  const existing = cart.value.get(cartKey);
  if (!existing) {
    return;
  }
  if (existing.count > 1) {
    existing.count--;
  } else {
    cart.value.delete(cartKey);
  }
  cart.value = new Map(cart.value);
};

const removeFromCart = (goods: GoodsItemFull) => {
  const matchedItems = cartItems.value.filter((item) => item.goodsId === goods.id);
  if (matchedItems.length === 0) {
    return;
  }
  if (normalizeGoodsSpecs(goods.specs).length > 0 && matchedItems.length > 1) {
    ElMessage.info('多规格商品请在右侧购物车中调整数量');
    return;
  }
  decreaseCartItem(matchedItems[0].cartKey);
};

// 声音控制
const soundEnabled = ref(true);
const orderAudio = new Audio('/new_order.mp3');
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let lastOrderIds = new Set<number>();
let isFirstLoad = true;

// 播放新订单提示音
const playNewOrderSound = () => {
  if (soundEnabled.value) {
    orderAudio.currentTime = 0;
    orderAudio.play().catch(() => {
      // 忽略自动播放限制错误
    });
  }
};

// 检查是否有新订单
const checkNewOrders = (orders: OrderItem[]) => {
  const currentIds = new Set(orders.map((item) => item.id));
  // 首次加载只记录ID，不判断为新订单
  if (isFirstLoad) {
    lastOrderIds = currentIds;
    isFirstLoad = false;
    return false;
  }
  let hasNew = false;
  for (const orderId of currentIds) {
    if (!lastOrderIds.has(orderId)) {
      hasNew = true;
      break;
    }
  }
  lastOrderIds = currentIds;
  return hasNew;
};

// 获取已占用座位数
const getOccupiedCount = (desk: DesktopItem) => {
  if (!desk.seats || desk.seats.length === 0) {
    return 0;
  }
  return desk.seats.filter((seat) => seat.status !== 'idle').length;
};

// 获取座位状态class: idle(空闲), reserved(已预约), playing(游戏中)
const getSeatStatus = (desk: DesktopItem, seatNum: number) => {
  const seat = desk.seats?.find((item) => item.seatNumber === seatNum);
  if (!seat) {
    return 'idle';
  }
  return seat.status;
};

// 获取座位用户名
const getSeatUserName = (desk: DesktopItem, seatNum: number) => {
  const seat = desk.seats?.find((item) => item.seatNumber === seatNum);
  if (!seat || seat.status === 'idle') {
    return '';
  }
  return seat.userInfo?.name || '-';
};

// 获取座位头像
const getSeatAvatar = (desk: DesktopItem, seatNum: number) => {
  const seat = desk.seats?.find((item) => item.seatNumber === seatNum);
  if (!seat || seat.status === 'idle') {
    return '';
  }
  const avatar = seat.userInfo?.avatar || '';
  if (!avatar || avatar.startsWith('http')) {
    return avatar;
  }
  return useApi().baseUrl + avatar;
};

// 获取座位手机号
const getSeatPhone = (desk: DesktopItem, seatNum: number) => {
  const seat = desk.seats?.find((item) => item.seatNumber === seatNum);
  if (!seat || seat.status === 'idle') {
    return '';
  }
  return seat.userInfo?.phone || '-';
};

// 获取今日统计
const fetchTodayStats = async() => {
  try {
    const res = await useApi().statisticsApi.getTodayStatisticsstatistics();
    todayIncome.value = res.data.data?.totalIncome || '0.00';
    todayOrderCount.value = res.data.data?.totalOrders || 0;
  } catch {
    todayIncome.value = '0.00';
    todayOrderCount.value = 0;
  }
};

// 获取桌面列表
const fetchDesktopList = async() => {
  try {
    desktopLoading.value = true;
    const res = await useApi().desktopApi.pageDesktopdesktop({
      conditions: { desktopType: 'reservation' as any },
      pageQuery: { page: 0, pageSize: 100 },
    });
    desktopList.value = (res.data.data?.data || []) as DesktopItem[];
  } finally {
    desktopLoading.value = false;
  }
};

// 获取点单桌列表（desktopType包含order_desk）
const fetchOrderDeskList = async() => {
  try {
    const res = await useApi().desktopApi.pageDesktopdesktop({
      conditions: { desktopType: 'order_desk' as any },
      pageQuery: { page: 0, pageSize: 100 },
    });
    orderDeskList.value = (res.data.data?.data || []) as DesktopItem[];
  } catch {
    orderDeskList.value = [];
  }
};

// 获取待出餐订单
const fetchPendingOrders = async(checkSound = false) => {
  try {
    orderLoading.value = true;
    const res = await useApi().billApi.listWaitingBillbill();
    const orders = (res.data.data || []) as OrderItem[];
    // 检测新订单并播放提示音
    if (checkSound && checkNewOrders(orders)) {
      playNewOrderSound();
    }
    pendingOrders.value = orders;
  } finally {
    orderLoading.value = false;
  }
};

// 获取候桌列表
const fetchWaitingList = async() => {
  try {
    const res = await useApi().waitingDeskApi.listWaitingDeskwaitingDesk({
      conditions: {},
    });
    waitingList.value = (res.data.data || []) as WaitingItem[];
  } catch {
    waitingList.value = [];
  }
};

// 刷新数据
const handleRefresh = () => {
  fetchTodayStats();
  fetchWaitingList();
  fetchDesktopList();
  fetchPendingOrders();
};

// 打开核销券弹窗
const handleVerifyCoupon = () => {
  couponId.value = '';
  couponInfo.value = null;
  verifyCouponForm.value = { desktopId: undefined, seatNumber: undefined };
  verifyCouponDlgVisible.value = true;
};

// 关闭核销券弹窗
const closeVerifyCouponDlg = () => {
  verifyCouponDlgVisible.value = false;
  couponId.value = '';
  couponInfo.value = null;
};

// 查询券
const handleQueryCoupon = async() => {
  if (!couponId.value) {
    ElMessage.warning('请输入券码');
    return;
  }
  couponLoading.value = true;
  try {
    const res = await useApi().couponApi.findCouponByIdcoupon({ couponId: Number(couponId.value) });
    if (res.data.data) {
      couponInfo.value = res.data.data;
    } else {
      ElMessage.warning('未找到该券');
      couponInfo.value = null;
    }
  } catch {
    ElMessage.error('查询失败');
    couponInfo.value = null;
  } finally {
    couponLoading.value = false;
  }
};

// 确认核销
const handleVerifyCouponSubmit = async() => {
  if (!couponInfo.value) {
    return;
  }
  // 有商品时需要选择桌和座位
  if (couponInfo.value.goods) {
    if (!verifyCouponForm.value.desktopId) {
      ElMessage.warning('请选择桌');
      return;
    }
    if (!verifyCouponForm.value.seatNumber) {
      ElMessage.warning('请选择座位');
      return;
    }
  }
  verifyLoading.value = true;
  try {
    await useApi().couponApi.verifyCouponcoupon({
      couponId: couponInfo.value.id,
      desktopId: verifyCouponForm.value.desktopId,
      seatNumber: verifyCouponForm.value.seatNumber,
    });
    ElMessage.success('核销成功');
    verifyCouponDlgVisible.value = false;
    couponInfo.value = null;
    couponId.value = '';
    verifyCouponForm.value = { desktopId: undefined, seatNumber: undefined };
    handleRefresh();
  } catch {
    ElMessage.error('核销失败');
  } finally {
    verifyLoading.value = false;
  }
};

// 获取存积分申请列表
const fetchDepositList = async() => {
  try {
    const res = await useApi().pointsApplyApi.pagePointsApplypointsApply({
      conditions: { type: 'deposit', status: 'pending' },
      pageQuery: { page: depositPage.value - 1, pageSize: 10 },
    });
    depositList.value = res.data.data?.data || [];
    depositTotal.value = res.data.data?.total || 0;
  } catch {
    depositList.value = []; depositTotal.value = 0;
  }
};

// 获取取积分申请列表
const fetchWithdrawList = async() => {
  try {
    const res = await useApi().pointsApplyApi.pagePointsApplypointsApply({
      conditions: { type: 'withdraw', status: 'pending' },
      pageQuery: { page: withdrawPage.value - 1, pageSize: 10 },
    });
    withdrawList.value = res.data.data?.data || [];
    withdrawTotal.value = res.data.data?.total || 0;
  } catch {
    withdrawList.value = []; withdrawTotal.value = 0;
  }
};

// 打开存积分审核弹窗
const handleDepositAudit = async() => {
  await fetchDepositList();
  depositDlgVisible.value = true;
};

// 打开取积分审核弹窗
const handleWithdrawAudit = async() => {
  await fetchWithdrawList();
  withdrawDlgVisible.value = true;
};

// 审批操作
const handleApprove = async(row: any, status: 'approved' | 'rejected') => {
  const statusText = status === 'approved' ? '通过' : '拒绝';
  try {
    let convertType: 'points' | 'coins' | undefined;

    // 存积分审核通过时，检查是否超过限额
    if (status === 'approved' && row.type === 'deposit') {
      const ruleRes = await useApi().pointsRuleApi.getPointsRulepointsRule();
      const limitPoints = Number(ruleRes.data.data?.limitPoints || 0);
      const applyPoints = Number(row.points || 0);

      if (limitPoints > 0 && applyPoints > limitPoints) {
        // 超过限额，需要选择转换类型
        const result = await ElMessageBox.confirm(
          `申请积分 ${applyPoints} 超过1:1兑换上限 ${limitPoints}，超出部分需要选择转换类型`,
          '选择转换类型',
          {
            distinguishCancelAndClose: true,
            confirmButtonText: '转换为积分',
            cancelButtonText: '转换为D币',
          },
        )
          .then(() => 'points')
          .catch((action) => (action === 'cancel' ? 'coins' : null));

        // 用户关闭弹窗
        if (!result) {
          return;
        }
        convertType = result as 'points' | 'coins';
      }
    }

    // 二次确认
    try {
      await ElMessageBox.confirm(
        `确定要${statusText}该申请吗？`,
        '确认操作',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch (error) {
      console.log(error);
      return;
    }

    // 调用接口
    if (convertType) {
      await useApi().pointsApplyApi.approvePointsApplypointsApply(row.id, {
        status,
        convertType,
      });
    } else {
      await useApi().pointsApplyApi.approvePointsApplypointsApply(row.id, { status });
    }

    ElMessage.success(`已${statusText}`);
    // 刷新对应列表
    if (row.type === 'deposit') {
      await fetchDepositList();
    } else {
      await fetchWithdrawList();
    }
  } catch {
    ElMessage.error('操作失败');
  }
};

const handleAdjustPoints = () => {
  adjustPointsForm.value = {
    userId: undefined,
    type: 'add',
    amount: 1,
    reason: '',
  };
  selectedUserInfo.value = null;
  adjustPointsUserOptions.value = [];
  adjustPointsDlgVisible.value = true;
};

// 搜索用户（积分调整用）
const searchUsersForPoints = async(query: string) => {
  if (!query) {
    adjustPointsUserOptions.value = [];
    return;
  }
  adjustPointsSearchLoading.value = true;
  try {
    const res = await useApi().userApi.pageUsersuser({
      conditions: { meta: `phone": "%${query}%"` as any },
      pageQuery: { page: 0, pageSize: 10 },
    });
    adjustPointsUserOptions.value = res.data.data?.data?.map((item: any) => ({
      label: `${item.meta?.name || '-'}(${item.meta?.phone || '-'})`,
      value: item.id,
      userInfo: {
        id: item.id,
        avatar: item.meta?.avatar ? getFullUrl(item.meta.avatar) : '',
        name: item.meta?.name || '',
        phone: item.meta?.phone || '',
        points: item.meta?.points || 0,
      },
    })) || [];
  } catch {
    adjustPointsUserOptions.value = [];
  } finally {
    adjustPointsSearchLoading.value = false;
  }
};

// 选择用户后显示信息
const handleSelectUserForPoints = (userId: number) => {
  const user = adjustPointsUserOptions.value.find((item) => item.value === userId);
  selectedUserInfo.value = user?.userInfo || null;
};

// 提交积分调整
const submitAdjustPoints = async() => {
  if (!adjustPointsForm.value.userId) {
    ElMessage.warning('请选择用户');
    return;
  }
  if (!adjustPointsForm.value.amount || adjustPointsForm.value.amount < 1) {
    ElMessage.warning('请输入有效的积分值');
    return;
  }
  if (!adjustPointsForm.value.reason) {
    ElMessage.warning('请输入调整原因');
    return;
  }
  const amount = adjustPointsForm.value.type === 'add'
    ? String(adjustPointsForm.value.amount)
    : String(-adjustPointsForm.value.amount);
  adjustPointsLoading.value = true;
  try {
    await useApi().userApi.changePointsuser({
      userId: adjustPointsForm.value.userId,
      amount,
      reason: adjustPointsForm.value.reason,
    });
    ElMessage.success('积分调整成功');
    adjustPointsDlgVisible.value = false;
  } catch {
    ElMessage.error('积分调整失败');
  } finally {
    adjustPointsLoading.value = false;
  }
};

// 搜索用户
const searchUsers = async(query: string) => {
  if (!query) {
    userOptions.value = [];
    return;
  }
  userSearchLoading.value = true;
  try {
    const res = await useApi().userApi.pageUsersuser({
      conditions: { meta: `phone": "%${query}%"` as any },
      pageQuery: { page: 0, pageSize: 10 },
    });
    userOptions.value = res.data.data?.data?.map((item: any) => ({
      label: `${item.meta?.name || '-'}(${item.meta?.phone || '-'})`,
      value: item.id,
      openid: item.username || '',
    })) || [];
  } catch {
    userOptions.value = [];
  } finally {
    userSearchLoading.value = false;
  }
};

// 获取分类列表
const fetchCategoryList = async() => {
  try {
    const res = await useApi().categoryApi.listCategorycategory({ conditions: { isShow: true } });
    categoryList.value = res.data.data?.map((item: any) => ({ id: item.id, name: item.name })) || [];
  } catch {
    categoryList.value = [];
  }
};

// 获取商品列表
const fetchGoodsList = async() => {
  try {
    const res = await useApi().goodsApi.listGoodsgoods({ conditions: {} });
    goodsList.value = res.data.data?.map((item: any) => ({
      id: item.id,
      name: item.name,
      price: item.price || '0',
      pointsPrice: item.isPointsExchange ? (item.price || '0') : '0',
      image: item.image,
      categoryId: item.categoryId,
      description: item.description || '',
      stock: item.stock ?? 999,
      isPointsExchange: item.isPointsExchange ?? null,
      specs: item.specs || [],
    })) || [];
  } catch {
    goodsList.value = [];
  }
};

// 打开手动下单弹窗
const handleManualOrder = async() => {
  manualOrderForm.value = {
    userId: undefined,
    desktopId: undefined,
    payType: 'cash',
    receivedAmount: 0,
    remark: '',
  };
  cart.value = new Map();
  currentCategoryIdx.value = 0;
  userOptions.value = [];
  await Promise.all([fetchCategoryList(), fetchGoodsList(), fetchOrderDeskList()]);
  manualOrderDlgVisible.value = true;
};

// 提交手动订单
const submitManualOrder = async() => {
  if (!manualOrderForm.value.userId) {
    ElMessage.warning('请选择用户');
    return;
  }
  if (!manualOrderForm.value.desktopId) {
    ElMessage.warning('请选择桌');
    return;
  }
  if (cart.value.size === 0) {
    ElMessage.warning('请选择商品');
    return;
  }
  // 从 userOptions 获取 openid
  const selectedUser = userOptions.value.find((item) => item.value === manualOrderForm.value.userId);
  const openid = selectedUser?.openid || '';

  const desk = orderDeskList.value.find((item) => item.id === manualOrderForm.value.desktopId);
  const seatInfo = desk?.name || '';
  const goodsArray = cartItems.value.map((item) => ({
    id: item.goodsId,
    name: item.name,
    price: item.price,
    pointsPrice: item.isPointsExchange ? (item.pointsPrice || item.price) : '0',
    image: item.image,
    description: item.description,
    isPointsExchange: item.isPointsExchange ?? false,
    selectedSpecs: item.selectedSpecs,
    specText: item.specText,
    count: item.count,
    total: item.isPointsExchange ? '0.00' : new Decimal(item.price || 0)
      .times(item.count)
      .toFixed(2),
    totalPoints: item.isPointsExchange
      ? new Decimal(item.pointsPrice || item.price || 0)
        .times(item.count)
        .toString()
      : '0',
  }));

  const billPrice = hasMoneyAmount(cartMoneyTotal.value)
    ? new Decimal(manualOrderForm.value.receivedAmount || 0).toFixed(2)
    : '0.00';
  const billPointsPrice = cartPointsTotal.value;
  const hasMoneyPayment = hasMoneyAmount(cartMoneyTotal.value);
  const hasPointsPayment = hasPointsAmount(cartPointsTotal.value);
  const { payType: selectedPayType } = manualOrderForm.value;
  let payType = selectedPayType;
  if (hasMoneyPayment) {
    payType = selectedPayType;
  } else if (hasPointsPayment) {
    payType = 'points';
  }

  manualOrderLoading.value = true;
  try {
    await useApi().billApi.addBillbill({
      billAdminVo: {
        name: '手动下单',
        price: billPrice,
        pointsPrice: billPointsPrice,
        payType: payType as any,
        remark: manualOrderForm.value.remark || undefined,
        seatInfo,
        goods: goodsArray,
        billType: 'order',
        userId: manualOrderForm.value.userId,
      },
      openid,
    });
    ElMessage.success('下单成功');
    manualOrderDlgVisible.value = false;
    handleRefresh();
  } catch {
    ElMessage.error('下单失败');
  } finally {
    manualOrderLoading.value = false;
  }
};

// 获取某桌可用座位
const getAvailableSeats = (desktopId: number) => {
  const desk = desktopList.value.find((item) => item.id === desktopId);
  if (!desk) {
    return [];
  }
  const occupiedSet = desk.seats?.filter((st) => st.status !== 'idle').map((st) => st.seatNumber) || [];
  return [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((num) => !occupiedSet.includes(num));
};

// 获取座位对应的 userId
const getSeatUserId = (desk: DesktopItem, seatNum: number) => desk.seats?.find((st) => st.seatNumber === seatNum)?.userId || 0;

// 打开上桌对话框（从座位点击）
const openSitDownDialog = (desk: DesktopItem, seatNum: number) => {
  const status = getSeatStatus(desk, seatNum);
  const isReserved = status === 'reserved';
  const seat = desk.seats?.find((st) => st.seatNumber === seatNum);
  sitDownForm.value = {
    desktopId: desk.id, seatNumber: seatNum,
    userId: isReserved ? (seat?.userId || undefined) : undefined,
    points: Number(desk.basePoints) || 0,
    isReserved,
    userName: isReserved ? (seat?.userInfo?.name || '-') : '',
  };
  sitDownDialogVisible.value = true;
};

// 执行上桌
const handleSitDown = async() => {
  const { desktopId, seatNumber, userId, points } = sitDownForm.value;
  if (!userId) {
    ElMessage.warning('请选择用户'); return;
  }
  try {
    await useApi().desktopApi.sitDowndesktop({ desktopId, seatNumber, userId, points: String(points) });
    ElMessage.success('上桌成功');
    sitDownDialogVisible.value = false;
    fetchDesktopList();
    fetchWaitingList();
  } catch {
    ElMessage.error('操作失败');
  }
};

// 执行离开
const handleStandUp = (desk: DesktopItem, seatNum: number) => {
  const userId = getSeatUserId(desk, seatNum);
  ElMessageBox.confirm('确定让该用户离开座位吗？', '确认离开', { type: 'warning' }).then(async() => {
    try {
      await useApi().desktopApi.standUpdesktop({ desktopId: desk.id, seatNumber: seatNum, userId });
      ElMessage.success('已离开');
      fetchDesktopList();
    } catch {
      ElMessage.error('操作失败');
    }
  })
    .catch(() => undefined);
};

// 打开候桌上桌对话框
const openWaitingSitDownDialog = (item: WaitingItem) => {
  waitingSitForm.value = {
    userId: item.userId, desktopId: undefined, seatNumber: undefined, points: 0,
    userName: `${item.userInfo?.name || '-'} (${item.userInfo?.phone || '-'})`,
    waitingId: item.id,
  };
  waitingSitDlgVisible.value = true;
};

// 执行候桌上桌
const handleWaitingSitDown = async() => {
  const { desktopId, seatNumber, userId, points } = waitingSitForm.value;
  if (!desktopId || !seatNumber) {
    ElMessage.warning('请选择桌子和座位'); return;
  }
  try {
    await useApi().desktopApi.sitDowndesktop({ desktopId, seatNumber, userId, points: String(points) });
    ElMessage.success('上桌成功');
    waitingSitDlgVisible.value = false;
    fetchDesktopList();
    fetchWaitingList();
  } catch {
    ElMessage.error('操作失败');
  }
};

// 清空座位
const handleClearSeats = (desk: DesktopItem) => {
  ElMessageBox.confirm(
    `确定要清空 ${desk.name} 的所有座位吗？`,
    '确认清空',
    { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' },
  ).then(async() => {
    try {
      await useApi().desktopApi.updateDesktopdesktop({ desktopAdminVo: { id: desk.id, seats: [] } });
      ElMessage.success('座位已清空');
      fetchDesktopList();
    } catch {
      ElMessage.error('操作失败');
    }
  });
};

const handleCancelOrder = (order: OrderItem) => {
  ElMessageBox.prompt('请输入取消原因', '取消订单', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPlaceholder: '请输入取消原因',
  }).then(async({ value }) => {
    try {
      await useApi().billApi.cancelBillbill({ billId: order.id, reason: value || '管理员取消' });
      ElMessage.success('订单已取消');
      fetchPendingOrders();
    } catch {
      ElMessage.error('取消失败');
    }
  });
};

const handleComplete = (order: OrderItem) => {
  ElMessageBox.confirm('确定已完成出餐？', '完成出餐', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'info',
  }).then(async() => {
    try {
      await useApi().billApi.completeOrderbill({ billId: order.id });
      ElMessage.success('出餐完成');
      fetchPendingOrders();
    } catch {
      ElMessage.error('操作失败');
    }
  });
};

const autoRefresh = () => {
  fetchTodayStats();
  fetchWaitingList();
  fetchDesktopList();
  fetchPendingOrders(true);
};

const startRefreshTimer = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }
  refreshTimer = setInterval(autoRefresh, 5000);
};
const stopRefreshTimer = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }
};

onMounted(() => {
  handleRefresh();
  startRefreshTimer();
});
onUnmounted(() => {
  stopRefreshTimer();
});
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
